import { GridColDef } from '@mui/x-data-grid';
import { AgencyOfficialRolesMap } from './index'
import { OfficialData } from '../../networkRequest/officialsService';
import { InviteStatus, InviteStatusType, JobStatus, JobStatusType } from './components/StatusChips';
import { OfficialAvatar } from '@components';
import DOMPurify from 'dompurify';
import { useUser } from '@hooks/useUser';
import { useInfo } from '@hooks/useInfo';
import { isRoleAssignable } from '../../utils/authorization';


const useColumns = (
  agencyOfficialRoles: AgencyOfficialRolesMap,
): GridColDef[] => {
  const { user } = useUser();
  const { employmentTypeOptions } = useInfo();

  const valueParser = (value) => DOMPurify.sanitize(value);

  return [
    {
      field: 'first_name',
      headerName: 'First Name',
      editable: true,
      valueParser,
      renderCell: (params: { row: OfficialData }) => (
        <OfficialAvatar
          img_url={params.row.img_url}
          name={params.row.first_name}
          square={false}
          officialTableView={true}
        />
      ),
      width: 150
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      editable: true,
      valueParser,
      width: 150
    },
    {
      field: 'title',
      headerName: 'Title',
      editable: true,
      valueParser,
      width: 225
    },
    {
      field: 'employment_type',
      headerName: 'Designation',
      editable: true,
      type: 'singleSelect',
      valueOptions: employmentTypeOptions,
      valueParser,
      width: 150
    },
    {
      field: 'role_name',
      headerName: 'Role',
      editable: true,
      type: 'singleSelect',
      valueOptions: Array.from(agencyOfficialRoles.keys())
        .filter((name) => isRoleAssignable(name, user?.profile)),
      valueParser,
      renderCell: (params: { row: OfficialData }) => params.row.role.name,
      width: 150
    },
    {
      field: 'invite_status',
      headerName: 'Invite Status',
      valueOptions: ['sent', 'not sent'],
      renderCell: (params: { row: { invite_status: InviteStatusType } }) => {
        return (
        <InviteStatus status={params.row.invite_status} />
      )},
      width: 150
    },
    {
      field: 'status',
      headerName: 'Job Status',
      editable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: 'active', label: <JobStatus status='active' /> },
        { value: 'suspended', label: <JobStatus status='suspended' /> },
        { value: 'deactivated', label: <JobStatus status='inactive' /> },
      ],
      valueParser,
      renderCell: (params: { row: OfficialData }) => (
        <JobStatus status={params.row.status as JobStatusType} />
      ),
      getApplyQuickFilterFn: (filterItem) => {
        return (params: string) => {
          return params.includes(filterItem);
        };
      },
      width: 150
    },
    {
      field: 'email',
      headerName: 'Email',
      editable: false,
      width: 225
    },
    {
      field: 'phone_number',
      headerName: 'Phone',
      editable: true,
      valueParser,
      width: 150
    },
    {
      field: 'rank',
      headerName: 'Rank',
      editable: true,
      valueParser,
      width: 150
    },
    {
      field: 'badge_number',
      headerName: 'Badge Number',
      editable: true,
      valueParser,
      width: 150
    },
    {
      field: 'armed_permission',
      headerName: 'Armed Permission',
      type: 'singleSelect',
      valueOptions: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
      ],
      editable: true,
      valueParser: (value) => value ? 'Yes' : 'No',
      width: 150
    },
    {
      field: 'verified',
      headerName: 'Verified',
      editable: false,
      renderCell: (params: { row: OfficialData }) => params.row.verified ? 'Yes' : 'No',
      width: 150
    }
  ];
};

export default useColumns;